import React, { useState, useEffect } from 'react';
import { Button, Modal } from '@/components/ui';
import type { ConstructionContract } from '../types/construction-contracts.types';
import { useConstructionContracts } from '../hooks/useConstructionContracts';
import { useToast } from '@/components/ui/Toast';
import { useUsers } from '@/modules/users/list/hooks/useUserList';

interface ContractTeamFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  contract: ConstructionContract;
  companyId: string;
}

export function ContractTeamFormModal({ isOpen, onClose, contract, companyId }: ContractTeamFormModalProps) {
  const { toast } = useToast();
  const { updateContract, isUpdating } = useConstructionContracts(companyId);
  const { data: users = [] } = useUsers(companyId);

  const [formData, setFormData] = useState<Partial<ConstructionContract>>({});

  useEffect(() => {
    if (isOpen && contract) {
      setFormData({
        pm_user_id: contract.pm_user_id || '',
        site_manager_user_id: contract.site_manager_user_id || '',
        qs_user_id: contract.qs_user_id || '',
      });
    }
  }, [isOpen, contract]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateContract({ id: contract.id, updates: formData });
      onClose();
      toast.success('Cập nhật đội ngũ phụ trách thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cập nhật Đội ngũ Phụ trách" size="md">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Project Manager (PM)</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.pm_user_id || ''}
              onChange={(e) => setFormData({ ...formData, pm_user_id: e.target.value })}
            >
              <option value="">-- Chọn PM --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Site Manager (SM)</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.site_manager_user_id || ''}
              onChange={(e) => setFormData({ ...formData, site_manager_user_id: e.target.value })}
            >
              <option value="">-- Chọn SM --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Quantity Surveyor (QS)</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.qs_user_id || ''}
              onChange={(e) => setFormData({ ...formData, qs_user_id: e.target.value })}
            >
              <option value="">-- Chọn QS --</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isUpdating}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isUpdating}>
            Lưu thay đổi
          </Button>
        </div>
      </form>
    </Modal>
  );
}
